import React, { useEffect, useRef, useState } from "react";
import YhInput from "../../../../../../ui/YhInput";
import YhButton from "../../../../../../ui/YhButton";
import YhTable, {
  YhTableColumn,
  YhTableCellParams,
} from "../../../../../../ui/YhTable";
import { applyLicenseKey } from "../../../../../../utils/License";
import AddZone from "./AddZone";
import { addZone, deleteZone, getZone } from "./ZoneService";
import ConfirmationModel from "../../../../../../global/components/ConfirmationModal/confirmationModel";

const SearchSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const ZoneList = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [metaData, setMetaData] = useState<any>({ total: 0 });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });
  const [openEdit, setOpenEdit] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(
    null
  );
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    applyLicenseKey();
  }, []);

  useEffect(() => {
    getAllZones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize, search, openEdit]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const getAllZones = async () => {
    try {
      setLoading(true);
      const payload = {
        search,
        limit: paginationModel.pageSize,
        page: paginationModel.page + 1,
      };
      const result = await getZone(payload);
      if (result?.success === 1) {
        setRows(result.data || []);
        setMetaData(result.metaData || { total: 0 });
      }
    } catch (error) {
      console.error("Error fetching zone data:", error);
    } finally {
      setLoading(false);
    }
  };

  const closeDrawer = () => {
    setOpenEdit(false);
    setIsEdit(false);
    setSelectedRow(null);
  };

  const deleteData = async (id: string) => {
    try {
      const response = await deleteZone(id);
      if (response.success === 1) {
        getAllZones();
        setDialogOpen(false);
        setSelectedRow(null);
      }
    } catch {
      /* ignore */
    }
  };

  const columns: YhTableColumn[] = [
    {
      headerName: "Zone",
      field: "zone_name",
      minWidth: 180,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-sm font-semibold capitalize text-[#111827]">
          {params.row?.zone_name || "—"}
        </span>
      ),
    },
    {
      headerName: "Country",
      field: "countryName",
      minWidth: 140,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-sm text-[#374151]">
          {params.row?.countryName || "—"}
        </span>
      ),
    },
    {
      headerName: "State",
      field: "state",
      minWidth: 140,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-sm text-[#374151]">{params.row?.state || "—"}</span>
      ),
    },
    {
      headerName: "City",
      field: "city",
      minWidth: 140,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-sm text-[#374151]">{params.row?.city || "—"}</span>
      ),
    },
    {
      headerName: "Popular",
      field: "isTop",
      minWidth: 120,
      renderCell: (params: YhTableCellParams) => {
        const checked = params.row.isTop === 1 || params.row.isTop === true;
        return (
          <label
            className="relative inline-flex h-6 w-11 cursor-pointer items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              className="peer sr-only"
              checked={!!checked}
              onChange={async (e) => {
                const updatedValue = e.target.checked;
                setRows((prev) =>
                  prev.map((row) =>
                    row._id === params.row._id
                      ? { ...row, isTop: updatedValue }
                      : row
                  )
                );
                try {
                  await addZone({
                    _id: params.row._id,
                    isTop: updatedValue,
                    countryId: params.row.countryId,
                    stateId: params.row.stateId,
                    cityId: params.row.cityId,
                    zone_name: params.row.zone_name,
                  });
                } catch {
                  setRows((prev) =>
                    prev.map((row) =>
                      row._id === params.row._id
                        ? { ...row, isTop: checked }
                        : row
                    )
                  );
                }
              }}
            />
            <span className="absolute inset-0 rounded-full bg-[#D1D5DB] transition peer-checked:bg-[#A02448]" />
            <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
          </label>
        );
      },
    },
    {
      headerName: "Action",
      field: "action",
      minWidth: 90,
      renderCell: (params: YhTableCellParams) => (
        <button
          type="button"
          data-action-button="true"
          className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F3F4F6]"
          onClick={(e) => {
            e.stopPropagation();
            const rect = (
              e.currentTarget as HTMLButtonElement
            ).getBoundingClientRect();
            setMenuPos({ top: rect.bottom + 4, left: rect.left - 160 });
            setSelectedRow(params.row._id);
            setMenuOpen(true);
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="5" r="1.5" />
            <circle cx="12" cy="12" r="1.5" />
            <circle cx="12" cy="19" r="1.5" />
          </svg>
        </button>
      ),
    },
  ];

  return (
    <div className="flex h-[calc(100vh-68px)] max-h-[calc(100vh-68px)] flex-col overflow-hidden bg-white p-4 sm:p-5 lg:p-6">
      <div className="mb-4 flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#1E1B4B] sm:text-3xl">
            Zone Master
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            Organization-wise zones for project details and website filters
          </p>
        </div>
        <YhButton
          variant="primary"
          size="sm"
          onClick={() => {
            setIsEdit(false);
            setSelectedRow(null);
            setOpenEdit(true);
          }}
        >
          + Create
        </YhButton>
      </div>

      <div className="mb-3 shrink-0 sm:max-w-[420px]">
        <YhInput
          placeholder="Search zones…"
          value={search}
          type="search"
          onChange={(e) => {
            setSearch(e.target.value);
            setPaginationModel((p) => ({ ...p, page: 0 }));
          }}
          startAdornment={<SearchSvg />}
        />
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden">
        <YhTable
          columns={columns}
          rows={rows}
          getRowId={(row) => row._id}
          loading={loading}
          emptyMessage="No zones found"
          className="h-full"
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          totalRows={metaData?.total || 0}
          pageSizeOptions={[10, 25, 50, 100, 200]}
          onPageChange={(p) =>
            setPaginationModel((prev) => ({ ...prev, page: p }))
          }
          onPageSizeChange={(s) =>
            setPaginationModel({ pageSize: s, page: 0 })
          }
        />
      </div>

      {menuOpen && menuPos ? (
        <div
          ref={menuRef}
          className="fixed z-[1500] w-48 overflow-hidden rounded-xl border border-[#EEF0F5] bg-white py-1 shadow-[0_8px_24px_rgba(15,23,42,0.12)]"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          <p className="px-3 py-2 text-xs font-semibold uppercase text-[#9CA3AF]">
            Actions
          </p>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#111827] hover:bg-[#F8FAFC]"
            onClick={() => {
              setIsEdit(true);
              setOpenEdit(true);
              setMenuOpen(false);
            }}
          >
            Edit
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#DC2626] hover:bg-[#FEF2F2]"
            onClick={() => {
              setDialogOpen(true);
              setMenuOpen(false);
            }}
          >
            Delete
          </button>
        </div>
      ) : null}

      {openEdit ? (
        <AddZone
          zoneId={selectedRow}
          closeDrawer={closeDrawer}
          isEdit={isEdit}
        />
      ) : null}

      <ConfirmationModel
        open={dialogOpen}
        title="Confirmation"
        description="Are you sure you want to delete?"
        onConfirm={() => selectedRow && deleteData(selectedRow)}
        onCancel={() => setDialogOpen(false)}
      />
    </div>
  );
};

export default ZoneList;
